import { TriangleAlert } from 'lucide-react'

import StatusScreen from '@/custolink-newdesign/components/StatusScreen'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/custolink-newdesign/ui/collapsible'

/**
 * Generic "something went wrong" page, new design.
 *
 * Used both for route errors (a failed loader, react-router's errorElement -
 * see RouteErrorBoundary.tsx) and for render crashes caught by
 * ErrorBoundary.tsx. Neither caller can guarantee the ui-context ever
 * loaded, so this must not call useUiContext or read org data - see the
 * header comment on StatusScreen.tsx.
 *
 * The raw message is the one thing the old ErrorPage.tsx always showed
 * (unconditionally, in a bare <pre>). Here it is dev-only and collapsed by
 * default: a crash is rarely actionable copy for an end user, and
 * import.meta.env.DEV is the app's existing dev-gating idiom (i18n.ts:67).
 *
 * `inline`: ErrorBoundary.tsx sets this - its fallback mounts inside
 * AppShell's content column, not standalone, so StatusScreen must fill that
 * column instead of claiming a fresh viewport. See StatusScreen's `fullPage`.
 */
export default function ErrorPageNewDesign(p: { message?: string; inline?: boolean }) {
  return (
    <StatusScreen
      tone="error"
      icon={<TriangleAlert className="cl:size-6" />}
      title="Something went wrong"
      description="An unexpected error occurred. Try again, or go back to the start."
      primaryLabel="Try again"
      secondaryLabel="Back"
      fullPage={!p.inline}
      details={
        import.meta.env.DEV && p.message ? (
          <Collapsible className="cl:mt-6 cl:text-left">
            <CollapsibleTrigger className="cl:text-muted-foreground cl:hover:text-foreground cl:text-xs cl:font-semibold cl:underline cl:underline-offset-2">
              Show error details
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="cl:bg-muted cl:text-muted-foreground cl:mt-2 cl:max-h-64 cl:overflow-auto cl:rounded-lg cl:p-3 cl:text-xs cl:whitespace-pre-wrap">
                {p.message}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        ) : null
      }
      onPrimary={() => window.location.reload()}
      onSecondary={() => window.history.back()}
    />
  )
}
